import type { Vec2 } from '../../core/model';
import type { Aabb } from './sceneLayout';

export interface SnapTargets {
  xs: number[];
  ys: number[];
}

export interface Guide {
  axis: 'x' | 'y';
  /** Canvas coordinate of the guide line on its axis. */
  at: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  guides: Guide[];
}

export interface SnapOptions {
  canvas: boolean;
  center: boolean;
  items: boolean;
}

/** Lines things may snap to: canvas edges and centre, and other items' edges and centres. */
export function collectTargets(canvas: Vec2, others: readonly Aabb[], options: SnapOptions): SnapTargets {
  const xs: number[] = [];
  const ys: number[] = [];
  if (options.canvas) {
    xs.push(0, canvas.x);
    ys.push(0, canvas.y);
  }
  if (options.center) {
    xs.push(canvas.x / 2);
    ys.push(canvas.y / 2);
  }
  if (options.items) {
    for (const box of others) {
      xs.push(box.minX, box.maxX);
      ys.push(box.minY, box.maxY);
      if (options.center) {
        xs.push((box.minX + box.maxX) / 2);
        ys.push((box.minY + box.maxY) / 2);
      }
    }
  }
  return { xs, ys };
}

/** Smallest correction bringing one of `values` onto one of `targets`, if any lies within `threshold`. */
export function snapAxis(
  values: readonly number[],
  targets: readonly number[],
  threshold: number,
): { delta: number; at: number } | null {
  let best: { delta: number; at: number } | null = null;
  for (const value of values) {
    for (const target of targets) {
      const delta = target - value;
      if (Math.abs(delta) <= threshold && (best === null || Math.abs(delta) < Math.abs(best.delta))) {
        best = { delta, at: target };
      }
    }
  }
  return best;
}

function combine(x: ReturnType<typeof snapAxis>, y: ReturnType<typeof snapAxis>): SnapResult {
  const guides: Guide[] = [];
  if (x) guides.push({ axis: 'x', at: x.at });
  if (y) guides.push({ axis: 'y', at: y.at });
  return { dx: x?.delta ?? 0, dy: y?.delta ?? 0, guides };
}

/** Snap a moving box by its edges and centre. */
export function snapBox(box: Aabb, targets: SnapTargets, threshold: number): SnapResult {
  return combine(
    snapAxis([box.minX, (box.minX + box.maxX) / 2, box.maxX], targets.xs, threshold),
    snapAxis([box.minY, (box.minY + box.maxY) / 2, box.maxY], targets.ys, threshold),
  );
}

/** Snap a single point (a resize handle); `axes` limits which axes may move. */
export function snapPoint(
  point: Vec2,
  targets: SnapTargets,
  threshold: number,
  axes: { x: boolean; y: boolean } = { x: true, y: true },
): SnapResult {
  return combine(
    axes.x ? snapAxis([point.x], targets.xs, threshold) : null,
    axes.y ? snapAxis([point.y], targets.ys, threshold) : null,
  );
}
